import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Check, X, Shield } from 'lucide-react';
import {
  SKIN_STATUS_OPTIONS,
  SOCKET_FIT_OPTIONS,
  PHANTOM_SENSATION_TAGS,
  todayLocalISO,
} from '../data/residualLimbLog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LIMB_CONSENT_KEY = 'enferno_limb_consent_v1';

const recordLimbConsent = () => {
  try { localStorage.setItem(LIMB_CONSENT_KEY, '1'); } catch { /* ignore */ }
  axios.patch(`${API}/account/consents`, { limb_health_data_collection: true }).catch(() => {});
};

const ResidualLimbLogModal = ({ open, onOpenChange, date, onSaved }) => {
  const targetDate = date || todayLocalISO();
  const [showLimbConsent, setShowLimbConsent] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [skinStatus, setSkinStatus] = useState('');
  const [skinNote, setSkinNote] = useState('');
  const [socketFit, setSocketFit] = useState('');
  const [socketLocation, setSocketLocation] = useState('');
  const [phantomLevel, setPhantomLevel] = useState(0);
  const [phantomSensations, setPhantomSensations] = useState([]);
  const [wearTime, setWearTime] = useState('');
  const [note, setNote] = useState('');

  useEffect(() => {
    if (!open) return;
    // Show consent notice on first ever open.
    const alreadyConsented = (() => { try { return !!localStorage.getItem(LIMB_CONSENT_KEY); } catch { return true; } })();
    if (!alreadyConsented) setShowLimbConsent(true);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/residual-limb-logs/${targetDate}`)
      .then((res) => {
        if (cancelled) return;
        const log = res.data?.log;
        if (log) {
          setSkinStatus(log.skin?.status || '');
          setSkinNote(log.skin?.note || '');
          setSocketFit(log.socket?.fit || '');
          setSocketLocation(log.socket?.location || '');
          setPhantomLevel(log.phantom?.level ?? 0);
          setPhantomSensations(log.phantom?.sensations || []);
          setWearTime(log.wear_time_hours != null ? String(log.wear_time_hours) : '');
          setNote(log.note || '');
        } else {
          setSkinStatus(''); setSkinNote('');
          setSocketFit(''); setSocketLocation('');
          setPhantomLevel(0); setPhantomSensations([]);
          setWearTime(''); setNote('');
        }
      })
      .catch(() => { /* missing log = fresh entry, leave blank */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, targetDate]);

  const toggleSensation = (tag) => {
    setPhantomSensations((current) =>
      current.includes(tag) ? current.filter((t) => t !== tag) : [...current, tag]
    );
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        skin: { status: skinStatus, note: skinNote },
        socket: { fit: socketFit, location: socketLocation },
        phantom: { level: Number(phantomLevel) || 0, sensations: phantomSensations },
        wear_time_hours: wearTime === '' ? null : Number(wearTime),
        note,
      };
      const res = await axios.put(`${API}/residual-limb-logs/${targetDate}`, payload);
      if (onSaved) onSaved(res.data?.log);
      onOpenChange(false);
    } catch {
      // surface error via toast pattern used elsewhere in the codebase
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="residual-limb-log-modal">
        <DialogHeader>
          <DialogTitle>Residual-limb check-in</DialogTitle>
          <DialogDescription>{targetDate} — log only what's relevant today. Everything is optional.</DialogDescription>
        </DialogHeader>

        {showLimbConsent && (
          <div className="flex items-start gap-3 p-3 mb-3 rounded-lg bg-enf-surface-mid border border-enf-surface-border" data-testid="limb-consent-notice">
            <Shield className="w-4 h-4 text-enf-role-info mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-xs text-enf-text-secondary leading-relaxed">
                Your residual-limb check-ins (skin, socket fit, phantom-limb sensations, wear time) help your coach — and, if you choose, your care team.
                What you log stays private, and it's yours to delete anytime in Profile.
              </p>
            </div>
            <button
              type="button"
              onClick={() => { recordLimbConsent(); setShowLimbConsent(false); }}
              data-testid="limb-consent-dismiss"
              className="text-[10px] font-semibold uppercase tracking-wider text-enf-role-info hover:text-enf-text-primary whitespace-nowrap flex-shrink-0 mt-0.5"
            >
              Got it
            </button>
          </div>
        )}
        {loading ? (
          <div className="text-sm text-enf-text-muted py-6 text-center">Loading…</div>
        ) : (
          <div className="space-y-5 pt-2">
            {/* Skin */}
            <div>
              <div className="text-xs font-semibold uppercase tracking-widest text-enf-text-secondary mb-2">Skin</div>
              <div className="flex flex-wrap gap-1.5">
                {SKIN_STATUS_OPTIONS.map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setSkinStatus(skinStatus === opt.id ? '' : opt.id)}
                    data-testid={`skin-status-${opt.id}`}
                    className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${
                      skinStatus === opt.id
                        ? 'bg-adaptive-accent/20 border-adaptive-accent/40 text-adaptive-accent'
                        : 'bg-enf-surface-mid border-enf-surface-border text-enf-text-secondary'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <textarea
                value={skinNote}
                onChange={(e) => setSkinNote(e.target.value)}
                placeholder="Notes (location, what triggered it, etc.)"
                rows={2}
                className="w-full mt-2 bg-enf-surface-mid border border-enf-surface-border rounded-md p-2 text-sm text-enf-text-primary placeholder-enf-text-muted"
              />
            </div>

            {/* Socket */}
            <div>
              <div className="text-xs font-semibold uppercase tracking-widest text-enf-text-secondary mb-2">Socket fit</div>
              <div className="flex flex-wrap gap-1.5">
                {SOCKET_FIT_OPTIONS.map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setSocketFit(socketFit === opt.id ? '' : opt.id)}
                    data-testid={`socket-fit-${opt.id}`}
                    className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${
                      socketFit === opt.id
                        ? 'bg-adaptive-accent/20 border-adaptive-accent/40 text-adaptive-accent'
                        : 'bg-enf-surface-mid border-enf-surface-border text-enf-text-secondary'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              {(socketFit === 'pain-point' || socketFit === 'tight') && (
                <input
                  value={socketLocation}
                  onChange={(e) => setSocketLocation(e.target.value)}
                  placeholder="Where? (e.g., distal end, posterior)"
                  className="w-full mt-2 bg-enf-surface-mid border border-enf-surface-border rounded-md p-2 text-sm text-enf-text-primary placeholder-enf-text-muted"
                />
              )}
            </div>

            {/* Phantom-limb pain */}
            <div>
              <div className="text-xs font-semibold uppercase tracking-widest text-enf-text-secondary mb-2">Phantom-limb pain</div>
              <div className="flex items-center gap-3">
                <input
                  type="range"
                  min="0"
                  max="10"
                  value={phantomLevel}
                  onChange={(e) => setPhantomLevel(Number(e.target.value))}
                  data-testid="phantom-level-slider"
                  className="flex-1"
                />
                <span className="text-sm font-mono text-enf-text-primary w-8 text-right">{phantomLevel}</span>
              </div>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {PHANTOM_SENSATION_TAGS.map((tag) => {
                  const active = phantomSensations.includes(tag);
                  return (
                    <button
                      key={tag}
                      type="button"
                      onClick={() => toggleSensation(tag)}
                      className={`px-2.5 py-1 rounded-full text-[11px] border transition-colors ${
                        active
                          ? 'bg-adaptive-accent/20 border-adaptive-accent/40 text-adaptive-accent'
                          : 'bg-enf-surface-mid border-enf-surface-border text-enf-text-muted'
                      }`}
                    >
                      {tag}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Wear time */}
            <div>
              <div className="text-xs font-semibold uppercase tracking-widest text-enf-text-secondary mb-2">Wear time today</div>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="0"
                  max="24"
                  step="0.5"
                  value={wearTime}
                  onChange={(e) => setWearTime(e.target.value)}
                  placeholder="0"
                  data-testid="wear-time-input"
                  className="w-24 bg-enf-surface-mid border border-enf-surface-border rounded-md p-2 text-sm text-enf-text-primary"
                />
                <span className="text-xs text-enf-text-muted">hours on prosthesis</span>
              </div>
            </div>

            {/* Free note */}
            <div>
              <div className="text-xs font-semibold uppercase tracking-widest text-enf-text-secondary mb-2">Note</div>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Anything else worth remembering"
                rows={3}
                className="w-full bg-enf-surface-mid border border-enf-surface-border rounded-md p-2 text-sm text-enf-text-primary placeholder-enf-text-muted"
              />
            </div>

            <div className="flex justify-end gap-2 pt-2 sticky bottom-0 bg-enf-surface-deep -mb-2 pb-2">
              <button
                type="button"
                onClick={() => onOpenChange(false)}
                className="px-4 py-2 rounded-md text-sm border border-enf-surface-border text-enf-text-secondary hover:text-enf-text-primary"
              >
                <X className="w-4 h-4 inline mr-1" /> Cancel
              </button>
              <button
                type="button"
                onClick={save}
                disabled={saving}
                data-testid="residual-limb-save-button"
                className="px-4 py-2 rounded-md text-sm font-semibold bg-adaptive-accent hover:bg-adaptive-accent-hover text-enf-text-primary disabled:opacity-50"
              >
                <Check className="w-4 h-4 inline mr-1" /> {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ResidualLimbLogModal;
